import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCreditCard,
  FiDollarSign,
  FiRefreshCcw,
  FiShoppingBag,
  FiSmartphone,
  FiUsers,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { getPosDailyReport } from "../../services/pos";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const todayInIndia = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());

const methodLabels = {
  CASH: "Cash",
  CARD: "Card",
  UPI: "UPI",
  BANK_TRANSFER: "Bank Transfer",
  STORE_CREDIT: "Store Credit",
  OTHER: "Other",
};

function StatCard({ title, value, description, icon: Icon, color }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">{value}</h2>
          <p className="mt-2 text-xs text-gray-400">{description}</p>
        </div>

        <div
          className={`grid h-12 w-12 place-items-center rounded-2xl ${color}`}
        >
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

export default function PosReportsPage() {
  const { accessToken } = useAuth();
  const [date, setDate] = useState(todayInIndia());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getPosDailyReport({ date }, accessToken);
      setReport(data);
    } catch (requestError) {
      setError(requestError.message || "Unable to load POS report");
    } finally {
      setLoading(false);
    }
  }, [accessToken, date]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const paymentRows = useMemo(() => {
    if (!report?.payments) return [];

    return Object.keys(report.payments.netByMethod).map((method) => ({
      method,
      sales: report.payments.salesByMethod[method] || 0,
      refunds: report.payments.refundsByMethod[method] || 0,
      net: report.payments.netByMethod[method] || 0,
    }));
  }, [report]);

  if (loading && !report) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  const summary = report?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-medium text-primary-800">Sales / POS</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            POS Daily Report
          </h1>
          <p className="mt-2 text-gray-500">
            Store, counter, cashier, payment, refund, and transaction activity.
          </p>
        </div>

        <div className="flex gap-3">
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-4 py-3 outline-none focus:border-primary-800"
          />

          <button
            type="button"
            onClick={loadReport}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-5 py-3 font-semibold text-white disabled:opacity-50"
          >
            <FiRefreshCcw className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Gross POS Sales"
          value={money(summary.grossSalesAmount)}
          description={`${summary.saleCount || 0} completed POS bills`}
          icon={FiShoppingBag}
          color="bg-primary-50 text-primary-800"
        />

        <StatCard
          title="POS Refunds"
          value={money(summary.totalRefundAmount)}
          description={`${summary.returnCount || 0} processed returns`}
          icon={FiRefreshCcw}
          color="bg-red-50 text-red-600"
        />

        <StatCard
          title="Net POS Sales"
          value={money(summary.netSalesAmount)}
          description={`GST included: ${money(summary.taxAmount)}`}
          icon={FiDollarSign}
          color="bg-emerald-50 text-emerald-600"
        />

        <StatCard
          title="Cash Change Given"
          value={money(summary.totalChangeAmount)}
          description="Cash handed back to customers"
          icon={FiCreditCard}
          color="bg-amber-50 text-amber-600"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-5">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm xl:col-span-2">
          <div className="mb-5">
            <h2 className="text-xl font-bold text-gray-900">
              Payment Reconciliation
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Sales less refunds by payment method.
            </p>
          </div>

          <div className="space-y-4">
            {paymentRows.map((item) => (
              <div
                key={item.method}
                className="rounded-2xl border border-gray-100 bg-gray-50 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-white text-primary-800 shadow-sm">
                      {item.method === "UPI" ? (
                        <FiSmartphone size={18} />
                      ) : (
                        <FiCreditCard size={18} />
                      )}
                    </div>

                    <p className="font-semibold text-gray-900">
                      {methodLabels[item.method] || item.method}
                    </p>
                  </div>

                  <p className="text-lg font-bold text-primary-800">
                    {money(item.net)}
                  </p>
                </div>

                <div className="mt-3 flex justify-between border-t border-gray-200 pt-3 text-xs">
                  <span className="text-gray-500">
                    Sales: {money(item.sales)}
                  </span>
                  <span className="text-red-600">
                    Refunds: -{money(item.refunds)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm xl:col-span-3">
          <div className="mb-5">
            <h2 className="text-xl font-bold text-gray-900">
              Counter Performance
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Net sales after POS returns.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3">Counter</th>
                  <th className="px-3 py-3">Store / Branch</th>
                  <th className="px-3 py-3 text-center">Sales</th>
                  <th className="px-3 py-3 text-center">Returns</th>
                  <th className="px-3 py-3 text-right">Gross</th>
                  <th className="px-3 py-3 text-right">Net</th>
                </tr>
              </thead>

              <tbody>
                {report?.counters?.length ? (
                  report.counters.map((counter) => (
                    <tr
                      key={counter.id}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="px-3 py-4">
                        <p className="font-semibold text-gray-900">
                          {counter.name}
                        </p>
                        <p className="mt-1 text-xs text-primary-800">
                          {counter.code}
                        </p>
                      </td>

                      <td className="px-3 py-4">
                        <p className="font-medium text-gray-800">
                          {counter.store.name}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {counter.store.branch?.name || "Unassigned branch"}
                        </p>
                      </td>

                      <td className="px-3 py-4 text-center">
                        {counter.saleCount}
                      </td>

                      <td className="px-3 py-4 text-center text-red-600">
                        {counter.returnCount}
                      </td>

                      <td className="px-3 py-4 text-right">
                        {money(counter.grossSalesAmount)}
                      </td>

                      <td className="px-3 py-4 text-right font-bold text-primary-800">
                        {money(counter.netSalesAmount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-14 text-center text-gray-500">
                      No POS activity for this date.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-5">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm xl:col-span-2">
          <div className="mb-5 flex items-center gap-3">
            <FiUsers className="text-primary-800" size={21} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Cashier Performance
              </h2>
              <p className="text-sm text-gray-500">
                Sales and returns handled today.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {report?.cashiers?.length ? (
              report.cashiers.map((cashier) => (
                <div
                  key={cashier.id}
                  className="flex items-center justify-between rounded-2xl border border-gray-100 p-4"
                >
                  <div>
                    <p className="font-semibold text-gray-900">
                      {cashier.fullName}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {cashier.employeeCode || "ERP User"} ·{" "}
                      {cashier.saleCount} sale
                      {cashier.saleCount === 1 ? "" : "s"} ·{" "}
                      {cashier.returnCount} return
                      {cashier.returnCount === 1 ? "" : "s"}
                    </p>
                  </div>

                  <p className="font-bold text-primary-800">
                    {money(cashier.netSalesAmount)}
                  </p>
                </div>
              ))
            ) : (
              <p className="py-10 text-center text-gray-500">
                No cashier activity for this date.
              </p>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm xl:col-span-3">
          <div className="mb-5">
            <h2 className="text-xl font-bold text-gray-900">
              Recent POS Bills
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Latest 20 non-void POS sales across all branches.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3">Receipt</th>
                  <th className="px-3 py-3">Store / Counter</th>
                  <th className="px-3 py-3">Cashier</th>
                  <th className="px-3 py-3">Time</th>
                  <th className="px-3 py-3 text-center">Status</th>
                  <th className="px-3 py-3 text-right">Total</th>
                </tr>
              </thead>

              <tbody>
                {report?.recentSales?.length ? (
                  report.recentSales.map((sale) => (
                    <tr
                      key={sale.id}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="px-3 py-4 font-mono text-xs font-bold text-primary-800">
                        {sale.receiptNumber}
                      </td>

                      <td className="px-3 py-4">
                        <p className="font-medium text-gray-900">
                          {sale.store.name}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {sale.counter.name}
                        </p>
                      </td>

                      <td className="px-3 py-4 text-gray-700">
                        {sale.cashier}
                      </td>

                      <td className="px-3 py-4 text-gray-500">
                        {new Date(sale.completedAt).toLocaleTimeString(
                          "en-IN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </td>

                      <td className="px-3 py-4 text-center">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-bold ${
                            sale.status === "RETURNED"
                              ? "bg-red-100 text-red-700"
                              : sale.status === "PARTIALLY_RETURNED"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-green-100 text-green-700"
                          }`}
                        >
                          {sale.status.replaceAll("_", " ")}
                        </span>
                      </td>

                      <td className="px-3 py-4 text-right font-bold text-primary-800">
                        {money(sale.totalAmount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-14 text-center text-gray-500">
                      No POS bills for this date.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
